// Minimal typed fetch wrapper. All paths are same-origin ("/api/...").

const TOKEN_KEY = "br.token";
let memoryToken: string | null = null;

export function getToken(): string | null {
  try {
    return window.localStorage.getItem(TOKEN_KEY) ?? memoryToken;
  } catch {
    return memoryToken;
  }
}

export function setToken(token: string | null): void {
  memoryToken = token;
  try {
    if (token) window.localStorage.setItem(TOKEN_KEY, token);
    else window.localStorage.removeItem(TOKEN_KEY);
  } catch {
    // Private mode or blocked storage: keep the in-memory copy only.
  }
}

export class ApiError extends Error {
  readonly status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

type UnauthorizedHandler = () => void;
let onUnauthorized: UnauthorizedHandler | null = null;
let onPasswordRequired: UnauthorizedHandler | null = null;

/** The server's 403 detail while a temporary password is still in place. */
export const PASSWORD_CHANGE_REQUIRED = "Set your own password before continuing";

/** Called when an authenticated request comes back 401 (token expired, reset or user removed). */
export function setUnauthorizedHandler(handler: UnauthorizedHandler | null): void {
  onUnauthorized = handler;
}

/** Called when the server says this session must set its own password first. */
export function setPasswordRequiredHandler(handler: UnauthorizedHandler | null): void {
  onPasswordRequired = handler;
}

interface RequestOptions {
  body?: unknown;
  signal?: AbortSignal;
  /** Send the bearer token (default true). */
  auth?: boolean;
  keepalive?: boolean;
  /** A 401 from this call means bad input (like a wrong current password), not a dead session. */
  ignore401?: boolean;
}

function describeStatus(status: number): string {
  if (status === 401) return "Your session ended. Sign in again.";
  if (status === 403) return "Only an owner can do that.";
  if (status === 404) return "Not found. It may have been deleted.";
  if (status === 409) return "That conflicts with the current state. Refresh and try again.";
  if (status === 429) return "Too many requests. Wait a moment and try again.";
  if (status >= 500) return `The server had a problem (${status}). Try again in a moment.`;
  return `Request failed (${status}).`;
}

export async function request<T>(
  method: "GET" | "POST" | "PATCH" | "DELETE",
  path: string,
  opts: RequestOptions = {},
): Promise<T> {
  const headers: Record<string, string> = { Accept: "application/json" };
  const useAuth = opts.auth !== false;
  const token = useAuth ? getToken() : null;
  if (token) headers.Authorization = `Bearer ${token}`;
  if (opts.body !== undefined) headers["Content-Type"] = "application/json";

  let res: Response;
  try {
    res = await fetch(path, {
      method,
      headers,
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
      signal: opts.signal,
      keepalive: opts.keepalive,
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === "AbortError") throw err;
    throw new ApiError(0, "Can’t reach the server. Check your connection and try again.");
  }

  if (res.status === 401 && token && onUnauthorized && !opts.ignore401) onUnauthorized();

  if (!res.ok) {
    let message = describeStatus(res.status);
    try {
      const data: unknown = await res.json();
      if (data && typeof data === "object" && "detail" in data) {
        const detail = (data as { detail: unknown }).detail;
        if (typeof detail === "string" && detail.trim()) message = detail;
        if (res.status === 403 && detail === PASSWORD_CHANGE_REQUIRED && token && onPasswordRequired) onPasswordRequired();
        // FastAPI validation errors come back as a list of {msg}.
        else if (Array.isArray(detail) && detail.length > 0) {
          const first = detail[0] as { msg?: unknown };
          if (typeof first?.msg === "string") message = first.msg;
        }
      }
    } catch {
      // Non-JSON error body: keep the generic message.
    }
    throw new ApiError(res.status, message);
  }

  if (res.status === 204) return undefined as T;
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

export function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) return err.message;
  return "Something went wrong. Try again.";
}

export function isAbort(err: unknown): boolean {
  return err instanceof DOMException && err.name === "AbortError";
}

/** Build a ws:// or wss:// URL for a same-origin socket path. */
export function wsUrl(path: string, params: Record<string, string>): string {
  const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
  const qs = new URLSearchParams(params).toString();
  return `${proto}//${window.location.host}${path}?${qs}`;
}
